import React, { useState } from 'react';
import { Plane, Hotel, Package, LifeBuoy, MapPin, Calendar, Tag, Search, Copy, CheckCircle, Menu, X } from 'lucide-react'; // Importing icons

// Main App component
const App = () => {
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  // States for passenger types
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);

  const [promoCode, setPromoCode] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [message, setMessage] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // State for mobile menu

  const handleSearch = (e) => {
    e.preventDefault();
    if (!origin || !destination || !departureDate) {
      setMessage('Please fill in origin, destination, and departure date.');
      setShowResults(false);
      return;
    }
    // Validation for passenger types
    if (adults < 1) {
      setMessage('At least one adult passenger is required.');
      setShowResults(false);
      return;
    }
    if (children < 0 || infants < 0) {
      setMessage('Passenger counts cannot be negative.');
      setShowResults(false);
      return;
    }

    setMessage('');
    setShowResults(true);
  };

  const handleApplyPromo = () => {
    if (promoCode.trim() === '') {
      setMessage('Please enter a promo code.');
      return;
    }
    setMessage(`Promo code "${promoCode}" applied! (This is a demo, actual application logic not implemented)`);
  };

  // Function to toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Example promo codes to display
  const examplePromoCodes = [
    { code: 'FLYSUMMER25', description: '25% off summer flights' },
    { code: 'TRAVELNOW10', description: '10% off any booking' },
    { code: 'FIRSTTRIP', description: 'Flat $50 off for new users' },
  ];

  const totalPassengers = adults + children + infants;

  return (
      <div
          className="min-h-screen flex flex-col items-center justify-center py-4 px-2 sm:py-8 sm:px-4"
          style={{
            background: 'linear-gradient(to bottom, #ACDF87, #D1EAF0)',
            fontFamily: 'Inter, sans-serif'
          }}
      >
        {/* Header */}
        <header className="w-full max-w-sm sm:max-w-4xl mb-4 sm:mb-8">
          <nav className="flex justify-between items-center py-3 px-4 sm:py-4 sm:px-6 bg-white bg-opacity-80 rounded-2xl shadow-lg relative">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-800 flex items-center">
              <Plane className="inline-block mr-1 sm:mr-2 text-[#ACDF87] w-6 h-6 sm:w-7 sm:h-7" /> Globe<span className="text-[#ACDF87]">Ease</span>
            </h1>

            {/* Hamburger menu icon for small screens */}
            <button className="sm:hidden text-gray-700" onClick={toggleMobileMenu}>
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>

            {/* Navigation links - hidden on small, flex on medium/large, visible when mobile menu open */}
            <ul className={`
            ${isMobileMenuOpen ? 'flex' : 'hidden'}
            flex-col absolute top-full left-0 w-full bg-white bg-opacity-90 rounded-b-lg shadow-lg py-4 z-10
            sm:flex sm:flex-row sm:static sm:w-auto sm:bg-transparent sm:rounded-none sm:shadow-none sm:py-0
            justify-center space-y-2 sm:space-y-0 space-x-0 sm:space-x-6 text-sm sm:text-base
          `}>
              <li><a href="#" className="flex items-center text-gray-700 hover:text-[#ACDF87] font-medium transition-colors duration-200 px-4 py-2 sm:p-0">
                <Plane size={16} className="mr-1" />Flights
              </a></li>
              <li><a href="#" className="flex items-center text-gray-700 hover:text-[#ACDF87] font-medium transition-colors duration-200 px-4 py-2 sm:p-0">
                <Hotel size={16} className="mr-1" />Hotels
              </a></li>
              <li><a href="#" className="flex items-center text-gray-700 hover:text-[#ACDF87] font-medium transition-colors duration-200 px-4 py-2 sm:p-0">
                <Package size={16} className="mr-1" />Packages
              </a></li>
              <li><a href="#" className="flex items-center text-gray-700 hover:text-[#ACDF87] font-medium transition-colors duration-200 px-4 py-2 sm:p-0">
                <LifeBuoy size={16} className="mr-1" />Support
              </a></li>
            </ul>
          </nav>
        </header>

        {/* Main Content Area */}
        <main className="w-full max-w-sm sm:max-w-4xl p-4 sm:p-8 bg-white bg-opacity-90 rounded-2xl shadow-xl flex flex-col space-y-6 sm:space-y-8">
          {/* Search Form */}
          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">Find Your Next Adventure</h2>
            <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label htmlFor="origin" className="block text-gray-700 text-base sm:text-lg font-medium mb-1 sm:mb-2 flex items-center">
                  <MapPin size={18} className="mr-1 sm:mr-2 text-[#ACDF87]" />Origin
                </label>
                <input
                    type="text"
                    id="origin"
                    className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ACDF87]"
                    placeholder="e.g., New York (NYC)"
                    value={origin}
                    onChange={(e) => setOrigin(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="destination" className="block text-gray-700 text-base sm:text-lg font-medium mb-1 sm:mb-2 flex items-center">
                  <MapPin size={18} className="mr-1 sm:mr-2 text-[#ACDF87]" />Destination
                </label>
                <input
                    type="text"
                    id="destination"
                    className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ACDF87]"
                    placeholder="e.g., Los Angeles (LAX)"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="departure-date" className="block text-gray-700 text-base sm:text-lg font-medium mb-1 sm:mb-2 flex items-center">
                  <Calendar size={18} className="mr-1 sm:mr-2 text-[#ACDF87]" />Departure Date
                </label>
                <input
                    type="date"
                    id="departure-date"
                    className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ACDF87]"
                    value={departureDate}
                    onChange={(e) => setDepartureDate(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="return-date" className="block text-gray-700 text-base sm:text-lg font-medium mb-1 sm:mb-2 flex items-center">
                  <Calendar size={18} className="mr-1 sm:mr-2 text-[#ACDF87]" />Return Date (Optional)
                </label>
                <input
                    type="date"
                    id="return-date"
                    className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ACDF87]"
                    value={returnDate}
                    onChange={(e) => setReturnDate(e.target.value)}
                />
              </div>
              {/* Passenger Count Inputs (Adults, Children, Infants) in a single row */}
              <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div>
                  <label htmlFor="adults" className="block text-gray-700 text-base sm:text-lg font-medium mb-1 sm:mb-2 flex items-center">
                    <Plane size={18} className="mr-1 sm:mr-2 text-[#ACDF87]" />Adults
                  </label>
                  <input
                      type="number"
                      id="adults"
                      className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ACDF87]"
                      min="1"
                      value={adults}
                      onChange={(e) => setAdults(Math.max(1, parseInt(e.target.value) || 1))}
                  />
                </div>
                <div>
                  <label htmlFor="children" className="block text-gray-700 text-base sm:text-lg font-medium mb-1 sm:mb-2 flex items-center">
                    <Plane size={18} className="mr-1 sm:mr-2 text-[#ACDF87]" />Children
                  </label>
                  <input
                      type="number"
                      id="children"
                      className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ACDF87]"
                      min="0"
                      value={children}
                      onChange={(e) => setChildren(Math.max(0, parseInt(e.target.value) || 0))}
                  />
                </div>
                <div>
                  <label htmlFor="infants" className="block text-gray-700 text-base sm:text-lg font-medium mb-1 sm:mb-2 flex items-center">
                    <Plane size={18} className="mr-1 sm:mr-2 text-[#ACDF87]" />Infants
                  </label>
                  <input
                      type="number"
                      id="infants"
                      className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ACDF87]"
                      min="0"
                      value={infants}
                      onChange={(e) => setInfants(Math.max(0, parseInt(e.target.value) || 0))}
                  />
                </div>
              </div>
              <div className="md:col-span-2 text-center">
                <button
                    type="submit"
                    className="
                  w-full px-8 py-3 sm:px-10 sm:py-4 bg-[#ACDF87] text-white font-bold text-lg sm:text-xl
                  rounded-full shadow-lg hover:shadow-xl transition-all duration-300
                  transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#ACDF87] focus:ring-opacity-50
                  flex items-center justify-center mx-auto
                "
                >
                  <Search size={20} className="mr-1 sm:mr-2" /> Search Flights
                </button>
              </div>
            </form>
            {message && (
                <p className="mt-4 text-center text-red-600 font-medium text-sm sm:text-base">{message}</p>
            )}
          </section>

          {/* Promo Code Application Section */}
          <section className="border-t pt-6 sm:pt-8 border-gray-200">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 text-center flex items-center justify-center">
              <Tag size={20} className="mr-1 sm:mr-2 text-[#ACDF87]" />Got a Promo Code?
            </h2>
            <div className="flex flex-col md:flex-row gap-3 sm:gap-4 justify-center items-center">
              <input
                  type="text"
                  className="flex-grow w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ACDF87]"
                  placeholder="Enter your promo code here"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
              />
              <button
                  onClick={handleApplyPromo}
                  className="
                w-full sm:w-auto px-6 py-2 sm:px-8 sm:py-3 bg-gray-700 text-white font-semibold rounded-full shadow-md
                hover:bg-gray-800 transition-colors duration-200 focus:outline-none focus:ring-2
                focus:ring-gray-700 focus:ring-opacity-75
                flex items-center justify-center
              "
              >
                <CheckCircle size={16} className="mr-1" /> Apply Code
              </button>
            </div>
          </section>

          {/* Example Promo Codes Section */}
          <section className="border-t pt-6 sm:pt-8 border-gray-200">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">Exclusive Deals Just For You!</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {examplePromoCodes.map((promo, index) => (
                  <div
                      key={index}
                      className="
                  bg-blue-50 p-4 sm:p-6 rounded-xl shadow-md text-center
                  border-2 border-[#D1EAF0] flex flex-col items-center justify-center
                  transform transition-transform duration-200 hover:scale-105
                "
                  >
                    <p className="text-sm text-gray-600 mb-1 sm:mb-2">{promo.description}</p>
                    <p className="text-2xl sm:text-3xl font-extrabold text-blue-700 tracking-wide mb-3 sm:mb-4">
                      {promo.code}
                    </p>
                    <button
                        onClick={() => { setPromoCode(promo.code); setMessage(`Promo code "${promo.code}" copied! Now apply it above.`); }}
                        className="
                    px-5 py-1.5 sm:px-6 sm:py-2 bg-[#ACDF87] text-white font-semibold rounded-full text-xs sm:text-sm
                    hover:bg-opacity-90 transition-colors duration-200
                    flex items-center justify-center mx-auto
                  "
                    >
                      <Copy size={14} className="mr-1" /> Copy Code
                    </button>
                  </div>
              ))}
            </div>
            <p className="mt-4 sm:mt-6 text-center text-gray-600 text-sm sm:text-base">
              Click on a code to copy it, then paste it in the "Got a Promo Code?" section above!
            </p>
          </section>

          {/* Flight Results Section (Conditional Rendering) */}
          {showResults && (
              <section className="border-t pt-6 sm:pt-8 border-gray-200">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">Available Flights</h2>
                <div className="grid grid-cols-1 gap-4 sm:gap-6">
                  {/* Example Flight Card 1 */}
                  <div className="bg-gray-50 p-4 sm:p-6 rounded-xl shadow-md flex flex-col sm:flex-row justify-between items-center text-center sm:text-left">
                    <div className="mb-2 sm:mb-0">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center justify-center sm:justify-start">
                        <Plane size={18} className="mr-1 sm:mr-2 text-[#ACDF87]" />NYC to LAX
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600">Departure: {departureDate}</p>
                      <p className="text-sm sm:text-base text-gray-600">Airline: AeroGreen Airlines</p>
                      <p className="text-sm sm:text-base text-gray-600">Passengers: {totalPassengers} (Adults: {adults}, Children: {children}, Infants: {infants})</p>
                    </div>
                    <div className="text-right mt-2 sm:mt-0">
                      <p className="text-xl sm:text-2xl font-bold text-[#ACDF87]">$299</p>
                      <button className="px-4 py-1.5 sm:px-5 sm:py-2 bg-[#ACDF87] text-white rounded-full hover:bg-opacity-90 transition-colors duration-200 text-sm sm:text-base">
                        Book Now
                      </button>
                    </div>
                  </div>

                  {/* Example Flight Card 2 */}
                  <div className="bg-gray-50 p-4 sm:p-6 rounded-xl shadow-md flex flex-col sm:flex-row justify-between items-center text-center sm:text-left">
                    <div className="mb-2 sm:mb-0">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center justify-center sm:justify-start">
                        <Plane size={18} className="mr-1 sm:mr-2 text-[#ACDF87]" />NYC to LAX
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600">Departure: {departureDate}</p>
                      <p className="text-sm sm:text-base text-gray-600">Airline: SkyVoyage</p>
                      <p className="text-sm sm:text-base text-gray-600">Passengers: {totalPassengers} (Adults: {adults}, Children: {children}, Infants: {infants})</p>
                    </div>
                    <div className="text-right mt-2 sm:mt-0">
                      <p className="text-xl sm:text-2xl font-bold text-[#ACDF87]">$350</p>
                      <button className="px-4 py-1.5 sm:px-5 sm:py-2 bg-[#ACDF87] text-white rounded-full hover:bg-opacity-90 transition-colors duration-200 text-sm sm:text-base">
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>
                <p className="mt-4 sm:mt-6 text-center text-gray-600 text-sm sm:text-base">
                  *Flight prices are illustrative and subject to change.
                </p>
              </section>
          )}
        </main>

        {/* Footer */}
        <footer className="w-full max-w-sm sm:max-w-4xl mt-4 sm:mt-8 py-4 px-4 sm:py-6 sm:px-6 bg-white bg-opacity-80 rounded-2xl shadow-lg text-center text-gray-700 text-sm sm:text-base">
          <p className="flex items-center justify-center">&copy; 2025 GlobeEase. All rights reserved.</p>
          <p className="text-xs sm:text-sm mt-1 sm:mt-2 flex items-center justify-center">
            Designed with <span className="text-red-500 mx-1">&hearts;</span> using the new gradient theme.
          </p>
        </footer>
      </div>
  );
};

export default App;
